export const OperatorLoginErrorType = {
  OperatorNotFound: 'OperatorNotFound',
  LocationForbidden: 'LocationForbidden',
  KeycloakPostError: 'KeycloakPostError',
  OperatorDisabled: 'OperatorDisabled',
  MaximumOperatorSessionsReached: 'MaximumOperatorSessionsReached',
  DeviceNotConnected: 'DeviceNotConnected'
} as const

export type OperatorLoginErrorCode = (typeof OperatorLoginErrorType)[keyof typeof OperatorLoginErrorType]

const OPERATOR_LOGIN_ERROR_TYPE_VALUES = new Set<string>(Object.values(OperatorLoginErrorType))

const operatorLoginErrorMessages: Record<OperatorLoginErrorCode, string> = {
  [OperatorLoginErrorType.OperatorNotFound]: 'Invalid operator credentials',
  [OperatorLoginErrorType.KeycloakPostError]: 'Invalid operator credentials',
  [OperatorLoginErrorType.LocationForbidden]:
    'Access denied. You are not authorized to log in from this location.',
  [OperatorLoginErrorType.MaximumOperatorSessionsReached]:
    'Access denied. You have reached the maximum number of concurrent device sessions. Log out from another device and try again.',
  [OperatorLoginErrorType.DeviceNotConnected]: 'Access denied. Device is not connected.',
  [OperatorLoginErrorType.OperatorDisabled]: 'Access denied. Account is currently disabled.'
}

const RESERVED_BODY_KEYS = new Set(['message', 'error', 'detail', 'error_description', 'messageError'])

const capitalizeFieldName = (field: string): string => field.charAt(0).toUpperCase() + field.slice(1)

const extractFieldValidationMessages = (body: unknown): string | undefined => {
  if (!body || typeof body !== 'object') {
    return undefined
  }

  const messages = Object.entries(body)
    .filter(([field]) => !RESERVED_BODY_KEYS.has(field))
    .flatMap(([field, value]) => {
      const label = capitalizeFieldName(field)

      if (Array.isArray(value)) {
        return value
          .filter((item): item is string => typeof item === 'string' && item.trim() !== '')
          .map(item => `${label}: ${item}`)
      }

      if (typeof value === 'string' && value.trim()) {
        return [`${label}: ${value}`]
      }

      return []
    })

  return messages.length ? messages.join(' ') : undefined
}

export const getOperatorLoginErrorType = (response: unknown): OperatorLoginErrorCode | undefined => {
  if (!response || typeof response !== 'object') {
    return undefined
  }

  if ('messageError' in response && typeof response.messageError === 'string') {
    const { messageError } = response
    if (OPERATOR_LOGIN_ERROR_TYPE_VALUES.has(messageError)) {
      return messageError as OperatorLoginErrorCode
    }
  }

  if ('password' in response) {
    return OperatorLoginErrorType.KeycloakPostError
  }

  return undefined
}

const ERROR_MESSAGE_KEYS = ['error_description', 'detail', 'message'] as const

const isRecord = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === 'object'

const getString = (body: Record<string, unknown>, key: string): string | undefined => {
  const value = body[key]
  return typeof value === 'string' && value.trim() ? value : undefined
}

export const formatDmCoreErrorMessage = (body: unknown, fallback = 'Request failed'): string =>
  isRecord(body) ? (ERROR_MESSAGE_KEYS.map(key => getString(body, key)).find(Boolean) ?? fallback) : fallback

export const formatOperatorLoginErrorMessage = (body: unknown, fallback = 'Operator login failed'): string => {
  const fieldValidationMessage = extractFieldValidationMessages(body)
  if (fieldValidationMessage) {
    return fieldValidationMessage
  }

  if (body && typeof body === 'object' && 'error_description' in body) {
    const errorDescription = body.error_description
    if (typeof errorDescription === 'string' && errorDescription.trim()) {
      return errorDescription
    }
  }

  const errorType = getOperatorLoginErrorType(body)
  if (errorType) {
    return operatorLoginErrorMessages[errorType]
  }

  return formatDmCoreErrorMessage(body, fallback)
}
